import { localDayISO } from './localDay.js'
import { derivedSprayDefault } from './wearDefaults.js'

// A wear counts as a gym wear if ANY of its gym-carrying fields say so. Gym
// wears are written with occasion 'Gym', wear_label 'gym' AND evening_context
// 'gym' (see HomePage.logGymScent / WearQuickCapture gym mode), but older rows
// may carry only one of these — mirror mostRecentGymFragranceId's broad read so
// history stays complete. Keep this in lockstep with that helper.
export function isGymWearLog(log) {
  const context = `${log?.wear_label || ''} ${log?.occasion || ''} ${log?.context || ''} ${log?.evening_context || ''}`
    .toLowerCase()
  return context.includes('gym')
}

function logDay(log) {
  return log?.worn_on || log?.wear_date || ''
}

// Newest application timestamp, else created_at, else the worn day at noon.
// Matches the ordering basis wearDefaults uses so shelf ranking and spray
// derivation agree on "most recent".
function timestampForLog(log) {
  const latestApplication = (log?.applications || []).reduce((latest, application) => {
    const timestamp = new Date(application.applied_at || 0).getTime()
    return timestamp > latest ? timestamp : latest
  }, 0)
  if (latestApplication) return latestApplication
  const fallback = new Date(log?.created_at || `${logDay(log) || '1970-01-01'}T12:00:00`).getTime()
  return Number.isNaN(fallback) ? 0 : fallback
}

function matchFragrance(fragrances, log) {
  const direct = fragrances.find(fragrance => fragrance.id === log.fragrance_id)
  if (direct) return direct
  const group = fragrances.find(fragrance => (fragrance.sizes || []).some(size => size.id === log.fragrance_id))
  if (group) return { ...group, id: log.fragrance_id, collection_group_id: group.id }
  return fragrances.find(fragrance =>
    String(fragrance.name || '').toLowerCase() === String(log.fragrance_name || '').toLowerCase()
    && String(fragrance.house || '').toLowerCase() === String(log.house || '').toLowerCase()
  ) || null
}

/**
 * The Home gym-card decision boundary — the gym analogue of selectSotdHero.
 * Returns the owned local-day gym wear (most recent when there is more than
 * one) resolved against the collection, or null when nothing was logged today.
 */
export function selectTodayGymWear({
  logs = [],
  fragrances = [],
  userId = null,
  localDay = localDayISO(),
} = {}) {
  const todays = logs
    .filter(log => (!userId || log?.user_id === userId) && logDay(log) === localDay && isGymWearLog(log) && log?.fragrance_id)
    .sort((a, b) => timestampForLog(b) - timestampForLog(a))
  const log = todays[0]
  if (!log) return null
  const fragrance = matchFragrance(fragrances, log)
  if (!fragrance) return null
  return { log, fragrance, appliedAt: timestampForLog(log) }
}

/**
 * Up to `limit` gym favorites for the shelf on the Log gym scent page.
 * Derived from the user's gym-occasion wear history — most-worn first, most
 * recent breaking ties — unioned with client pins (gym_fragrance_ids) and minus
 * client-hidden ids. Pins are never dropped by the cap and never hidden.
 */
export function buildGymShelf({
  logs = [],
  fragrances = [],
  pinnedIds = [],
  hiddenIds = [],
  userId = null,
  limit = 5,
} = {}) {
  const pinned = new Set(pinnedIds)
  const hidden = new Set(hiddenIds.filter(id => !pinned.has(id)))

  const stats = new Map()
  for (const log of logs) {
    if (userId && log?.user_id !== userId) continue
    if (!isGymWearLog(log) || !log?.fragrance_id) continue
    const timestamp = timestampForLog(log)
    const current = stats.get(log.fragrance_id) || { wears: 0, lastWornAt: 0 }
    current.wears += 1
    if (timestamp > current.lastWornAt) current.lastWornAt = timestamp
    stats.set(log.fragrance_id, current)
  }

  const historyIds = [...stats.keys()].sort((a, b) => {
    const A = stats.get(a)
    const B = stats.get(b)
    if (B.wears !== A.wears) return B.wears - A.wears
    return B.lastWornAt - A.lastWornAt
  })

  // History first, then any pins that have no gym history yet (in pin order).
  const orderedIds = [...historyIds]
  for (const id of pinnedIds) {
    if (!stats.has(id)) orderedIds.push(id)
  }

  const cards = orderedIds
    .filter(id => !hidden.has(id))
    .map(id => {
      const fragrance = fragrances.find(item => item.id === id) || null
      if (!fragrance) return null
      const stat = stats.get(id) || { wears: 0, lastWornAt: 0 }
      return {
        id,
        fragrance,
        wears: stat.wears,
        lastWornAt: stat.lastWornAt || null,
        pinned: pinned.has(id),
        spraysDefault: derivedSprayDefault(logs, id, 3),
      }
    })
    .filter(Boolean)

  if (cards.length <= limit) return cards

  // Over the cap: keep every pin, then fill remaining slots with the
  // highest-ranked unpinned cards, preserving the history ordering.
  const pinnedCards = cards.filter(card => card.pinned)
  const room = Math.max(0, limit - pinnedCards.length)
  const keptUnpinned = new Set(cards.filter(card => !card.pinned).slice(0, room))
  return cards.filter(card => card.pinned || keptUnpinned.has(card))
}
